import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Alert, CircularProgress, Button, Chip, TextField, IconButton, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { reportingUsageAPI } from '../services/reportingUsageApi';
import { errText, limit, limitGb } from './ReportingUsageParts';

const today = () => new Date().toISOString().slice(0, 10);
const NATIVE = { inputLabel: { shrink: true } };
const SELECT = { select: { native: true }, inputLabel: { shrink: true } };

// A customer's licence: the bundle in force, top-ups, combined limits and bundle history. canEdit (Cloud Services administrators) adds the forms.
export default function ReportingAllocations({ custCode, canEdit = false }) {
  const [alloc, setAlloc] = useState(null);
  const [bundles, setBundles] = useState([]);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);
  const [bundleId, setBundleId] = useState('');
  const [validFrom, setValidFrom] = useState(today());
  const [licenceStart, setLicenceStart] = useState('');
  const [showTopup, setShowTopup] = useState(false);
  const [topup, setTopup] = useState({ label: '', test_limit: '', storage_limit_gb: '', valid_from: today() });

  const load = useCallback(() => {
    reportingUsageAPI.activeAllocations(custCode)
      .then((res) => { setAlloc(res.data); setBundleId(res.data.bundle?.bundle_id ? String(res.data.bundle.bundle_id) : ''); setLicenceStart(res.data.licence_start_date || ''); setError(''); })
      .catch((err) => { setAlloc(null); setError(errText(err, 'Could not load the licence')); });
  }, [custCode]);

  useEffect(() => { setAlloc(null); setHistory(null); load(); }, [load]);
  useEffect(() => {
    if (!canEdit) return;
    reportingUsageAPI.bundles(true).then((res) => setBundles(res.data || [])).catch(() => setBundles([]));
  }, [canEdit]);

  const act = async (fn, done) => {
    setSaving(true); setError('');
    try { await fn(); setFlash(done); setHistory(null); load(); }
    catch (err) { const d = err.response?.data?.detail; setError(Array.isArray(d) ? 'Check the values entered' : errText(err, 'Could not save')); }
    finally { setSaving(false); }
  };

  const assign = () => act(() => reportingUsageAPI.assignBundle(custCode, { bundle_id: Number(bundleId), valid_from: validFrom || today(), licence_start_date: licenceStart || null }), 'Bundle assigned');
  const addTopup = () => act(async () => {
    await reportingUsageAPI.addTopup(custCode, {
      label: topup.label.trim(), test_limit: topup.test_limit ? Number(topup.test_limit) : null,
      storage_limit_gb: topup.storage_limit_gb ? Number(topup.storage_limit_gb) : null, valid_from: topup.valid_from || today(),
    });
    setTopup({ label: '', test_limit: '', storage_limit_gb: '', valid_from: today() });
    setShowTopup(false);
  }, 'Top-up added');
  const closeTopup = (id) => act(() => reportingUsageAPI.closeAllocation(id, {}), 'Top-up ended');
  const toggleHistory = async () => {
    if (history) { setHistory(null); return; }
    try { const res = await reportingUsageAPI.allocations(custCode); setHistory((res.data || []).filter((a) => a.allocation_type === 'bundle')); }
    catch (err) { setError(errText(err, 'Could not load the bundle history')); }
  };

  if (!alloc && !error) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>;
  const { bundle, topups = [], totals = {} } = alloc || {};
  const hasTotals = totals.sample_limit != null || totals.test_limit != null || totals.storage_limit_gb != null;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {flash && <Alert severity="success" onClose={() => setFlash('')} sx={{ mb: 1.5 }}>{flash}</Alert>}
      {alloc && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
            <Typography variant="subtitle2" gutterBottom>Bundle and licence</Typography>
            {bundle ? (
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{bundle.bundle_name || bundle.label}</Typography>
                  <Chip size="small" color="primary" label="Active bundle" />
                </Box>
                <Typography variant="caption" color="text.secondary" component="div">From {bundle.valid_from}{bundle.valid_to ? ` to ${bundle.valid_to}` : ' (open-ended)'}</Typography>
                <Typography variant="body2">Samples: {limit(bundle.sample_limit)} | Tests: {limit(bundle.test_limit)} | Storage: {limitGb(bundle.storage_limit_gb)}</Typography>
              </Box>
            ) : <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>No bundle is assigned today.</Typography>}
            {alloc.licence_start_date && <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>Licence year starts {alloc.licence_start_date}</Typography>}
            {hasTotals && (
              <Alert severity="success" icon={false} sx={{ mb: 1.5, py: 0 }}>
                <Typography variant="subtitle2">Combined limits</Typography>
                <Typography variant="body2">Samples: {limit(totals.sample_limit)} | Tests: {limit(totals.test_limit)} | Storage: {limitGb(totals.storage_limit_gb)}</Typography>
              </Alert>
            )}
            {canEdit && (
              <Box sx={{ display: 'grid', gap: 1.5, mb: 1 }}>
                <TextField select size="small" label={bundle ? 'Change bundle' : 'Assign bundle'} value={bundleId} onChange={(e) => setBundleId(e.target.value)} slotProps={SELECT}>
                  <option value="">Select a bundle</option>
                  {bundles.map((b) => <option key={b.id} value={String(b.id)}>{b.name}</option>)}
                </TextField>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <TextField type="date" size="small" label="Valid from" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} slotProps={NATIVE} />
                  <TextField type="date" size="small" label="Licence year start" value={licenceStart} onChange={(e) => setLicenceStart(e.target.value)} slotProps={NATIVE} />
                </Box>
                <Box><Button size="small" variant="contained" disabled={!bundleId || saving} onClick={assign}>{bundle ? 'Change bundle' : 'Assign bundle'}</Button></Box>
              </Box>
            )}
            <Button size="small" onClick={toggleHistory}>{history ? 'Hide bundle history' : 'Bundle history'}</Button>
            {history && (
              history.length === 0 ? <Typography variant="caption" color="text.secondary" component="div">No bundle history found.</Typography> : (
                <Table size="small" aria-label="Bundle history">
                  <TableHead><TableRow><TableCell>Bundle</TableCell><TableCell>From</TableCell><TableCell>To</TableCell><TableCell align="right">Samples</TableCell><TableCell align="right">Tests</TableCell><TableCell align="right">Storage</TableCell></TableRow></TableHead>
                  <TableBody>
                    {history.map((h) => (
                      <TableRow key={h.id}>
                        <TableCell>{h.bundle_name || h.label}</TableCell><TableCell>{h.valid_from}</TableCell><TableCell>{h.valid_to || 'Active'}</TableCell>
                        <TableCell align="right">{limit(h.sample_limit)}</TableCell><TableCell align="right">{limit(h.test_limit)}</TableCell><TableCell align="right">{limitGb(h.storage_limit_gb)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Top-ups</Typography>
              {canEdit && <Button size="small" onClick={() => setShowTopup((v) => !v)}>{showTopup ? 'Cancel' : 'Add top-up'}</Button>}
            </Box>
            {canEdit && showTopup && (
              <Box sx={{ display: 'grid', gap: 1.5, mb: 1.5 }}>
                <TextField size="small" required label="Label" placeholder="e.g. Storage top-up Apr 2025" value={topup.label} onChange={(e) => setTopup((t) => ({ ...t, label: e.target.value }))} slotProps={NATIVE} />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <TextField type="number" size="small" label="Extra tests" value={topup.test_limit} onChange={(e) => setTopup((t) => ({ ...t, test_limit: e.target.value }))} slotProps={{ ...NATIVE, htmlInput: { min: 0 } }} />
                  <TextField type="number" size="small" label="Extra storage (GB)" value={topup.storage_limit_gb} onChange={(e) => setTopup((t) => ({ ...t, storage_limit_gb: e.target.value }))} slotProps={{ ...NATIVE, htmlInput: { min: 0, step: 'any' } }} />
                </Box>
                <TextField type="date" size="small" label="Purchase date" value={topup.valid_from} onChange={(e) => setTopup((t) => ({ ...t, valid_from: e.target.value }))} slotProps={NATIVE} />
                <Box><Button size="small" variant="contained" disabled={!topup.label.trim() || (!topup.test_limit && !topup.storage_limit_gb) || saving} onClick={addTopup}>Save top-up</Button></Box>
              </Box>
            )}
            {topups.length === 0 && <Typography variant="body2" color="text.secondary">No top-ups recorded.</Typography>}
            {topups.map((t) => (
              <Paper key={t.id} variant="outlined" sx={{ p: 1, mb: 1, position: 'relative' }}>
                {canEdit && (
                  <Tooltip title="End this top-up today">
                    <IconButton size="small" sx={{ position: 'absolute', top: 2, right: 2 }} aria-label={`End top-up ${t.label}`} disabled={saving} onClick={() => closeTopup(t.id)}><CloseIcon fontSize="small" /></IconButton>
                  </Tooltip>
                )}
                <Typography variant="body2" sx={{ fontWeight: 600, pr: 4 }}>{t.label}</Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', my: 0.5 }}>
                  {t.test_limit ? <Chip size="small" color="success" variant="outlined" label={`+${Number(t.test_limit).toLocaleString()} tests`} /> : null}
                  {t.storage_limit_gb ? <Chip size="small" color="secondary" variant="outlined" label={`+${Number(t.storage_limit_gb).toLocaleString()} GB`} /> : null}
                </Box>
                <Typography variant="caption" color="text.secondary">From {t.valid_from}{t.valid_to ? ` to ${t.valid_to}` : ''} | Added by {t.created_by || '-'}</Typography>
              </Paper>
            ))}
          </Paper>
        </Box>
      )}
    </Box>
  );
}
